import { useEffect, useMemo, useState } from "react";
import "./style.css";

const normalizeId = (value) => {
  if (!value) return "";
  if (typeof value === "object") {
    if (value._id) return normalizeId(value._id);
    if (value.id) return normalizeId(value.id);
  }
  return String(value);
};

const formatDisplayName = (member) => {
  if (!member) return "Member";
  const full = [member.first_name, member.last_name]
    .filter(Boolean)
    .join(" ")
    .trim();
  return full || member.name || member.username || "Member";
};

export default function LeaveGroupModal({
  conversation,
  currentUser,
  members = [],
  isCurrentUserOwner = false,
  isVisible,
  onClose,
  onConfirm,
  onDisband,
}) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [selectedNewAdminId, setSelectedNewAdminId] = useState("");

  const currentUserId = normalizeId(currentUser?.id || currentUser?._id);

  const transferableMembers = useMemo(() => {
    if (!Array.isArray(members)) return [];
    return members.filter((m) => {
      const id = normalizeId(m?._id || m?.id);
      return id && id !== currentUserId;
    });
  }, [members, currentUserId]);

  // Reset transient state when the modal closes or the member list changes
  // (e.g., a removal happened while the modal was open).
  useEffect(() => {
    if (!isVisible) {
      setSelectedNewAdminId("");
      return;
    }
    // If the previously selected admin is no longer a valid member, clear it.
    setSelectedNewAdminId((prev) => {
      if (!prev) return prev;
      const stillThere = transferableMembers.some(
        (m) => normalizeId(m?._id || m?.id) === prev
      );
      return stillThere ? prev : "";
    });
  }, [isVisible, transferableMembers]);

  if (!isVisible) return null;

  const groupName = conversation?.name || "this group";
  const ownerAlone = isCurrentUserOwner && transferableMembers.length === 0;
  const ownerNeedsTransfer =
    isCurrentUserOwner && transferableMembers.length > 0;

  const handleClose = () => {
    if (isSubmitting) return;
    onClose && onClose();
  };

  const handleConfirm = async () => {
    if (isSubmitting) return;
    try {
      setIsSubmitting(true);
      if (ownerAlone) {
        if (onDisband) await onDisband();
      } else if (ownerNeedsTransfer) {
        if (!selectedNewAdminId) return;
        if (onConfirm) await onConfirm({ transferToUserId: selectedNewAdminId });
      } else {
        if (onConfirm) await onConfirm({});
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  // ---------- Render helpers ----------
  const renderRegularLeaveContent = () => (
    <div className="leave_group_content">
      <div className="leave_group_icon" aria-hidden="true">🚪</div>
      <p className="leave_group_description">
        Are you sure you want to leave <strong>{groupName}</strong>?
      </p>
      <p className="leave_group_note">
        You won't be able to send or receive messages after leaving. Your chat
        history will remain read-only.
      </p>
    </div>
  );

  const renderOwnerAloneContent = () => (
    <div className="leave_group_content">
      <div className="leave_group_icon" aria-hidden="true">⚠️</div>
      <p className="leave_group_description">
        You're the only member of <strong>{groupName}</strong>.
      </p>
      <p className="leave_group_note">
        Leaving will permanently disband the group and delete its chat
        history. This action cannot be undone.
      </p>
    </div>
  );

  const renderOwnerTransferContent = () => (
    <div className="leave_group_content leave_group_content--transfer">
      <div className="leave_group_icon" aria-hidden="true">👑</div>
      <p className="leave_group_description">
        Choose a new admin for <strong>{groupName}</strong>
      </p>
      <p className="leave_group_note">
        You're the current admin. Assign admin access to another member
        before leaving the group.
      </p>

      <div
        className="transfer_admin_list"
        role="radiogroup"
        aria-label="Select a new admin"
      >
        {transferableMembers.map((member) => {
          const memberId = normalizeId(member?._id || member?.id);
          const displayName = formatDisplayName(member);
          const isSelected = selectedNewAdminId === memberId;
          return (
            <label
              key={memberId}
              className={`transfer_admin_item ${isSelected ? "is_selected" : ""}`}
            >
              <input
                type="radio"
                name="new-admin"
                className="transfer_admin_radio"
                value={memberId}
                checked={isSelected}
                onChange={() => setSelectedNewAdminId(memberId)}
                disabled={isSubmitting}
              />
              <img
                src={member?.picture || "/images/default_profile.png"}
                alt={displayName}
                className="transfer_admin_avatar"
              />
              <div className="transfer_admin_info">
                <span className="transfer_admin_name">{displayName}</span>
                {member?.username && (
                  <span className="transfer_admin_username">@{member.username}</span>
                )}
              </div>
              <span
                className="transfer_admin_check"
                aria-hidden="true"
              >
                {isSelected ? "●" : "○"}
              </span>
            </label>
          );
        })}
      </div>
    </div>
  );

  const confirmLabel = ownerAlone
    ? isSubmitting
      ? "Disbanding…"
      : "Disband Group"
    : ownerNeedsTransfer
      ? isSubmitting
        ? "Transferring & leaving…"
        : "Transfer & Leave"
      : isSubmitting
        ? "Leaving…"
        : "Leave Group";

  const isConfirmDisabled =
    isSubmitting || (ownerNeedsTransfer && !selectedNewAdminId);

  return (
    <div
      className="leave_group_modal_overlay"
      onClick={isSubmitting ? undefined : handleClose}
    >
      <div className="leave_group_modal" onClick={(e) => e.stopPropagation()}>
        <div className="leave_group_header">
          <h3>
            {ownerAlone
              ? "Disband Group"
              : ownerNeedsTransfer
                ? "Transfer Admin"
                : "Leave Group"}
          </h3>
          <button
            className="close_btn"
            onClick={handleClose}
            disabled={isSubmitting}
            aria-label="Close leave group dialog"
          >
            <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
              <path d="M19 6.41L17.59 5 12 10.59 6.41 5 5 6.41 10.59 12 5 17.59 6.41 19 12 13.41 17.59 19 19 17.59 13.41 12z" />
            </svg>
          </button>
        </div>

        {ownerAlone
          ? renderOwnerAloneContent()
          : ownerNeedsTransfer
            ? renderOwnerTransferContent()
            : renderRegularLeaveContent()}

        <div className="leave_group_actions">
          <button
            className="cancel_btn"
            onClick={handleClose}
            disabled={isSubmitting}
          >
            Cancel
          </button>
          <button
            className="leave_btn delete_btn"
            onClick={handleConfirm}
            disabled={isConfirmDisabled}
          >
            {confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
